// The strings grid (spec/dashboard.md section 9.1): Key, Group, Description, one column per enabled language, Export, the
// short id; every cell edit is a staged change of the string's draft; search on Enter and the Group / Coverage / Export
// filters live in the URL; + Create stages a new row on top with the humanised key as the default value's placeholder.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useContent } from '../../content/ContentContext';
import { CellTextEditor } from '../../content/localization/CellTextEditor';
import { humanizeKey, languageHeader } from '../../content/localization/languages';
import { createStringDraft, DEFAULT_GROUP, editString, fullKeyOf, hasText, ordinal, removeString, useStringRows, valuesOf, type StringRow } from '../../content/localization/strings';
import { useGame } from '../../shell/GameContext';
import { Button, IconButton } from '../../ui/Button';
import { Badge, draftTone } from '../../ui/Chip';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { AlertCircle, Download, Languages, MoreVertical, Plus, Search, Upload } from '../../ui/icons';
import { Select, Switch } from '../../ui/inputs';
import { useUrlParam } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { EmptyState } from '../../ui/Banner';
import { useWorkspace } from '../../workspace/react';
import { isTempId } from '../../workspace/Workspace';
import { GroupPicker } from './GroupPicker';

export function StringsTab() {
  const { can } = useGame();
  const catalog = useContent();
  const workspace = useWorkspace();
  const canWrite = can('content_write') && !workspace.isProtected;
  const [params, setParams] = useSearchParams();
  const stringParam = useUrlParam('string');
  const dialogParam = useUrlParam('dialog');
  const { rows, draftOf } = useStringRows(catalog);
  const languages = catalog.languages;
  const def = catalog.defaultLanguage;

  const q = params.get('q') ?? '';
  const groupFilter = params.get('group') ?? '';
  const missingFilter = params.get('missing') ?? '';
  const exportFilter = params.get('export') ?? '';
  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };

  // the row + Create (or the groups tab's + Group) just staged: its key cell takes the focus
  const [focusId, setFocusId] = useState<string | null>(null);
  const create = (group?: string) => {
    const id = createStringDraft(workspace, group || groupFilter || DEFAULT_GROUP, catalog.defaultLanguage);
    setFocusId(id);
  };
  const created = useRef(false);
  useEffect(() => {
    // `?create=1` (from the groups tab's + Group): one new row in the typed group, then the flag leaves the URL
    if (params.get('create') !== '1' || created.current) return;
    created.current = true;
    if (canWrite) create(params.get('group') || undefined);
    setParam({ create: null });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => Array.from(new Set([...catalog.groups, ...rows.map((r) => r.group)])).filter(Boolean).sort(ordinal), [catalog.groups, rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (groupFilter && r.group !== groupFilter) return false;
      if (missingFilter && hasText(r, missingFilter)) return false;
      if (exportFilter === 'exported' && r.export === false) return false;
      if (exportFilter === 'hidden' && r.export !== false) return false;
      if (!needle) return true;
      const hay = [r.key, fullKeyOf(r), r.description ?? '', ...Object.values(valuesOf(r))];
      return hay.some((h) => h.toLowerCase().includes(needle));
    });
  }, [rows, q, groupFilter, missingFilter, exportFilter]);

  const edit = (row: StringRow, path: string, value: unknown) => editString(workspace, catalog, row, path, value);
  const isNew = (row: StringRow) => draftOf(row.id)?.status === 'new';
  const isDeleted = (row: StringRow) => draftOf(row.id)?.status === 'deleted';
  const writable = (row: StringRow) => canWrite && !isDeleted(row);

  const columns: Column<StringRow>[] = [
    {
      key: 'key',
      header: 'Key',
      width: 220,
      sortValue: (r) => r.key,
      cellText: (r) => r.key,
      render: (r) =>
        isNew(r) && canWrite ? (
          <span className="loc-required-cell">
            <CellTextEditor value={r.key} autoFocus={focusId === r.id} aria-label="Key" placeholder="KEY_NAME" invalid={!r.key} onCommit={(v) => edit(r, 'key', v.trim())} />
            {!r.key && <span className="loc-required">required</span>}
          </span>
        ) : (
          <code className="loc-key">{r.key}</code>
        ),
      editor: canWrite ? (r, done) => (isNew(r) || !writable(r) ? <code className="loc-key">{r.key}</code> : <CellTextEditor value={r.key} autoFocus aria-label="Key" onCommit={(v) => { if (v.trim()) edit(r, 'key', v.trim()); done.commit(v); }} onCancel={done.cancel} />) : undefined,
    },
    {
      key: 'group',
      header: 'Group',
      width: 140,
      sortValue: (r) => r.group,
      cellText: (r) => r.group,
      render: (r) => r.group,
      editor: canWrite ? (r, done) => (writable(r) ? <GroupPicker value={r.group} groups={groups} autoFocus onCommit={(g) => { edit(r, 'group', g); done.commit(g); }} onCancel={done.cancel} /> : r.group) : undefined,
    },
    {
      key: 'description',
      header: 'Description',
      width: 200,
      cellText: (r) => r.description ?? '',
      render: (r) => <span className="muted">{r.description ?? ''}</span>,
      editor: canWrite ? (r, done) => (writable(r) ? <CellTextEditor value={r.description ?? ''} autoFocus aria-label="Description" onCommit={(v) => { edit(r, 'description', v === '' ? null : v); done.commit(v); }} onCancel={done.cancel} /> : (r.description ?? '')) : undefined,
    },
    ...languages.map<Column<StringRow>>((l) => {
      const header = `${languageHeader(l.code, l.name)}${l.code === def ? ' *' : ''}`;
      return {
        key: `values.${l.code}`,
        header,
        width: 220,
        cellText: (r) => valuesOf(r)[l.code] ?? '',
        render: (r) => {
          const text = valuesOf(r)[l.code] ?? '';
          if (l.code === def && isNew(r) && canWrite && !text) {
            // a new row's default value: the humanised key is only a placeholder; nothing is written until typed (09 §3.2)
            return (
              <span className="loc-required-cell">
                <CellTextEditor value="" multiline aria-label={header} placeholder={humanizeKey(r.key)} onCommit={(v) => v !== '' && edit(r, `values.${l.code}`, v)} />
                <span className="loc-required">required</span>
              </span>
            );
          }
          return text ? <span className="loc-text">{text}</span> : <span className="loc-missing">missing</span>;
        },
        editor: canWrite
          ? (r, done) =>
              writable(r) ? (
                <CellTextEditor
                  value={valuesOf(r)[l.code] ?? ''}
                  multiline
                  autoFocus
                  aria-label={header}
                  placeholder={l.code === def ? humanizeKey(r.key) : undefined}
                  onCommit={(v) => {
                    edit(r, `values.${l.code}`, v);
                    done.commit(v);
                  }}
                  onCancel={done.cancel}
                />
              ) : (
                (valuesOf(r)[l.code] ?? '')
              )
          : undefined,
      };
    }),
    {
      key: 'export',
      header: 'Export',
      width: 80,
      sortValue: (r) => (r.export === false ? 0 : 1),
      cellText: (r) => (r.export === false ? 'FALSE' : 'TRUE'),
      render: (r) => <Switch aria-label={`Export ${fullKeyOf(r)}`} checked={r.export !== false} disabled={!writable(r)} onChange={(v) => edit(r, 'export', v)} />,
    },
    {
      key: 'id',
      header: 'Id',
      width: 120,
      fixed: true,
      cellText: (r) => r.id,
      render: (r) => {
        const d = draftOf(r.id);
        return (
          <span className="row">
            <button type="button" className="btn btn-ghost btn-s loc-id-button" onClick={() => stringParam.open(r.id)} aria-label={`Open ${fullKeyOf(r)}`} title={r.id}>
              <code>{isTempId(r.id) ? r.id : r.id.slice(0, 8)}</code>
            </button>
            {d && <Badge tone={draftTone[d.status]}>{d.status === 'new' ? 'NEW' : d.status === 'deleted' ? 'DEL' : 'MOD'}</Badge>}
            {d?.error && (
              <Badge tone="danger" title={d.error.message}>
                <AlertCircle aria-hidden="true" />
                Error
              </Badge>
            )}
          </span>
        );
      },
    },
    {
      key: 'menu',
      header: '',
      width: 40,
      fixed: true,
      render: (r) => (
        <RowMenu
          row={r}
          canWrite={writable(r)}
          onOpen={() => stringParam.open(r.id)}
          onWhereUsed={() => stringParam.open(r.id, { where: '1' })}
          onExport={() => edit(r, 'export', r.export === false)}
          onDelete={() => {
            removeString(workspace, catalog, r);
            if (stringParam.value === r.id) stringParam.close();
          }}
        />
      ),
    },
  ];

  const filtersActive = !!(q || groupFilter || missingFilter || exportFilter);
  const toolbar = (
    <span className="row wrap loc-filters">
      <Select size="s" aria-label="Group filter" value={groupFilter} onChange={(e) => setParam({ group: e.target.value })}>
        <option value="">All groups</option>
        {groups.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </Select>
      <Select size="s" aria-label="Coverage filter" value={missingFilter} onChange={(e) => setParam({ missing: e.target.value })}>
        <option value="">All coverage</option>
        {languages.map((l) => (
          <option key={l.code} value={l.code}>
            missing in {languageHeader(l.code, l.name)}
          </option>
        ))}
      </Select>
      <Select size="s" aria-label="Export filter" value={exportFilter} onChange={(e) => setParam({ export: e.target.value })}>
        <option value="">Exported and hidden</option>
        <option value="exported">Exported</option>
        <option value="hidden">Hidden</option>
      </Select>
      {filtersActive && (
        <Button size="s" variant="ghost" onClick={() => setParam({ q: null, group: null, missing: null, export: null })}>
          Clear filters
        </Button>
      )}
    </span>
  );

  return (
    <div className="loc-strings">
      <div className="page-toolbar loc-actions">
        <span className="muted small">Edits are staged; Save All commits them.</span>
        <span className="spacer" />
        <Button onClick={() => dialogParam.open('import')} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected} icon={<Upload aria-hidden="true" />}>
          Import…
        </Button>
        <Button onClick={() => dialogParam.open('export')} icon={<Download aria-hidden="true" />}>
          Export…
        </Button>
        <Button variant="primary" aria-label="New string" onClick={() => create()} missingPermission={can('content_write') ? null : 'content_write'} disabled={workspace.isProtected} title={workspace.isProtected ? 'Protected branch: changes land through merge requests.' : undefined} icon={<Plus aria-hidden="true" />}>
          Create
        </Button>
      </div>
      <div data-testid="strings-grid">
        <DataGrid
          id="localization-strings"
          rows={filtered}
          rowKey={(r) => r.id}
          columns={columns}
          revision={catalog.version}
          loading={catalog.loading && rows.length === 0}
          caption={`${filtered.length} string${filtered.length === 1 ? '' : 's'}${filtered.length !== rows.length ? ` of ${rows.length}` : ''}`}
          toolbar={toolbar}
          search={{ value: q, onSearch: (text) => setParam({ q: text.trim() }), placeholder: 'Search keys, descriptions, texts…' }}
          pageResetKey={[groupFilter, missingFilter, exportFilter].join('|')}
          revealKey={focusId}
          onRowOpen={(r) => stringParam.open(r.id)}
          rowClass={(r) => {
            const d = draftOf(r.id);
            return d ? (d.status === 'deleted' ? 'deleted' : 'dirty') : '';
          }}
          emptyState={
            catalog.error ? (
              <EmptyState icon={<AlertCircle />} title="The strings could not be loaded" body={catalog.error} />
            ) : filtersActive ? (
              <EmptyState icon={<Search />} title="No string matches" body="Change the search or the filters." />
            ) : (
              <EmptyState icon={<Languages />} title="No localization strings yet" body="Create one, or import a CSV or a Google Sheet." action={canWrite ? <Button size="s" onClick={() => create()} icon={<Plus aria-hidden="true" />}>Create</Button> : undefined} />
            )
          }
        />
      </div>
    </div>
  );
}

function RowMenu({ row, canWrite, onOpen, onWhereUsed, onExport, onDelete }: { row: StringRow; canWrite: boolean; onOpen: () => void; onWhereUsed: () => void; onExport: () => void; onDelete: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton
        label={`Actions for ${fullKeyOf(row)}`}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
      >
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu
        anchor={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label="String actions"
        items={[
          { key: 'open', label: 'Open', onSelect: onOpen },
          { key: 'where', label: 'Where used', onSelect: onWhereUsed, disabled: isTempId(row.id) },
          { key: 'export', label: row.export === false ? 'Export: off → on' : 'Export: on → off', onSelect: onExport, disabled: !canWrite },
          { key: 's1', label: '', separator: true },
          { key: 'delete', label: 'Delete', onSelect: onDelete, danger: true, disabled: !canWrite },
        ]}
      />
    </>
  );
}
